import { useRef, useEffect, useState } from 'react';

const PlacesAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter location", 
  className = "custom-input af",
  disabled = false,
  onPlaceSelect = null 
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check if Google Maps API is loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      initializeAutocomplete();
    } else {
      // Wait for Google Maps API to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
          initializeAutocomplete();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => {
        clearInterval(checkGoogleMaps);
      }, 10000);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google || !window.google.maps || !window.google.maps.places) {
      console.warn('Google Maps API not loaded or input ref not available');
      return;
    }

    try {
      // Create autocomplete instance
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['establishment', 'geocode'],
        componentRestrictions: { country: ['pk', 'ae'] }, // Restrict to Pakistan and UAE
        fields: ['place_id', 'formatted_address', 'geometry', 'name', 'types'],
        language: 'en',
        components: 'country:pak'
      });

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (place && place.formatted_address) {
          const address = place.formatted_address;
          onChange(address);
          
          // Call onPlaceSelect callback if provided
          if (onPlaceSelect) {
            onPlaceSelect({
              address: address,
              placeId: place.place_id,
              geometry: place.geometry,
              name: place.name,
              types: place.types
            });
          }
        } else {
          console.warn('No place selected or place data incomplete');
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  };

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e) => {
    // Prevent form submission when pressing Enter on autocomplete
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <div className="places-autocomplete-container">
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {!isLoaded && (
        <div className="places-loading-indicator">
          Loading places...
        </div>
      )}
      
      {/* Fallback message if Google Maps API fails to load */}
      {!isLoaded && (
        <div className="places-fallback-message">
          <small className="text-muted">
            Places autocomplete unavailable. You can still type the location manually.
          </small>
        </div>
      )}
      
      <style jsx>{`
        .places-autocomplete-container {
          position: relative;
          width: 100%;
        }
        
        .places-loading-indicator {
          position: absolute;
          top: 50%;
          right: 10px;
          transform: translateY(-50%);
          font-size: 12px;
          color: #666;
          pointer-events: none;
        }
        
        .places-fallback-message {
          margin-top: 4px;
          padding: 4px 8px;
          background-color: #f8f9fa;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }
        
        /* Override Google Places Autocomplete styles */
        .pac-container {
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 1px solid #e0e0e0;
          margin-top: 4px;
          z-index: 1000;
        }
        
        .pac-item {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .pac-item:hover {
          background-color: #f8f9fa;
        }
        
        .pac-item:last-child {
          border-bottom: none;
        }
        
        .pac-item-selected {
          background-color: #e8f5e9;
        }
        
        .pac-matched {
          font-weight: 600;
          color: #28a745;
        }
        
        .pac-item-query {
          font-size: 14px;
          color: #333;
        }
        
        .pac-item-query .pac-matched {
          color: #28a745;
        }
        
        .pac-item-description {
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }
        
        /* Custom input styling to match existing design */
        .custom-input.af {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
          background-color: #fff;
        }
        
        .custom-input.af:focus {
          outline: none;
          border-color: #28a745;
          box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
        }
        
        .custom-input.af:disabled {
          background-color: #f5f5f5;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .custom-input.af::placeholder {
          color: #999;
        }
      `}</style>
    </div>
  );
};

export default PlacesAutocomplete;
